import React from 'react';

import { FeedbackFlag } from '@atlaskit/feedback-collector';
import Flag, { FlagGroup } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { TeamsAnchor } from '@atlaskit/teams-app-internal-navigation/teams-anchor';
import {
	Anchor,
	FEEDBACK_COLLECTOR_SUCCESS_FLAG_ID,
	type Flag as IFlag,
} from '@atlassian/ptc-common';

import GenericErrorFlag from './GenericErrorFlag';
import type { FlagType } from './GenericErrorFlag/types';
import { getTypeIcon } from './GenericErrorFlag/utils';

export const GENERIC_ERROR_FLAG_ID = 'genericError';

interface Props {
	flags: IFlag[];
	removeFlag: (flagId: string | number) => void;
}

export const Flags = ({ flags, removeFlag }: Props): React.JSX.Element => {
	const handleDismissFlag = (id: string | number) => {
		removeFlag(id);
	};

	const parsedFlags = React.useMemo(
		() =>
			flags.map((flagProps) => {
				const { id, type, icon, appearance, title, description, links = [], actions } = flagProps;

				if (typeof id === 'string') {
					if (id.startsWith(FEEDBACK_COLLECTOR_SUCCESS_FLAG_ID)) {
						return <FeedbackFlag key={id} />;
					} else if (id.startsWith(GENERIC_ERROR_FLAG_ID)) {
						return <GenericErrorFlag key={id} id={id} />;
					}
				}

				// we cannot get props type of AutoDismissFlag so we have to use any here
				const flagComponentProps = {
					id,
					icon: icon || getTypeIcon(type || (appearance as FlagType)),
					title,
					description,
					appearance,
					actions:
						actions ||
						links.map(({ text, ...props }) => {
							const linkHref = props.href || props.to;
							return {
								content: fg('ptc-react-router-dom-links-migration') ? (
									linkHref && (
										<TeamsAnchor href={linkHref} intent="unknown">
											{text}
										</TeamsAnchor>
									)
								) : (
									<Anchor {...props}>{text}</Anchor>
								),
							};
						}),
				};

				return <Flag key={id} {...flagComponentProps} />;
			}),
		[flags],
	);

	return <FlagGroup onDismissed={handleDismissFlag}>{parsedFlags}</FlagGroup>;
};
